import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import {
  ArrowRight, Phone, CheckCircle2, Activity, Stethoscope, Heart,
  Home as HomeIcon, Dumbbell, ChevronRight, MapPin, Clock, Shield, Award,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { servicesData } from '../data/servicesData';

const iconMap = {
  Activity,
  Stethoscope,
  Heart,
  Home: HomeIcon,
  Dumbbell,
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = servicesData[slug];

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const Icon = iconMap[service.icon] || Activity;

  const relatedServices = Object.entries(servicesData)
    .filter(([s]) => s !== slug)
    .slice(0, 6);

  // Service Schema markup
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": service.name,
    "description": service.overview,
    "provider": {
      "@type": "MedicalBusiness",
      "name": "MSK and Sports Physio",
      "telephone": "+447747075809",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "HarroWall Climbing Centre, Neptune Trading Estate, Neptune Rd",
        "addressLocality": "Harrow",
        "postalCode": "HA1 4HX",
        "addressCountry": "GB",
      },
    },
    "areaServed": {
      "@type": "City",
      "name": "Harrow",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": service.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={service.title}
        description={service.metaDescription}
        keywords={service.keywords}
        type="website"
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="pt-24 pb-2 px-4 lg:px-8 bg-gray-50" aria-label="Breadcrumb">
        <div className="container mx-auto max-w-6xl">
          <ol className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <li><Link to="/" className="hover:text-blue-600">Home</Link></li>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <li><Link to="/" className="hover:text-blue-600">Services</Link></li>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <li className="text-gray-900 font-medium">{service.name}</li>
          </ol>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-10 md:py-16 px-4 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto max-w-5xl">
          <div className="flex items-start gap-4 md:gap-6 mb-6">
            <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
              <Icon className="w-7 h-7 md:w-8 md:h-8 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-1">
                Our Services
              </p>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
                {service.name} in Harrow
              </h1>
            </div>
          </div>
          <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8 max-w-3xl">
            {service.heroLine}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="service-hero-book-btn"
              onClick={() => window.location.href = '/contact'}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-5 text-base font-semibold rounded-lg shadow-lg"
            >
              Book Your Appointment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => window.location.href = 'tel:07747075809'}
              size="lg"
              variant="outline"
              className="px-6 py-5 text-base font-semibold rounded-lg border-2 border-gray-300 hover:border-blue-600"
            >
              <Phone className="mr-2 w-5 h-5" />
              07747075809
            </Button>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 md:py-16 px-4 lg:px-8">
        <div className="container mx-auto max-w-5xl">
          {/* Overview */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              About Our {service.name} Service
            </h2>
            <p className="text-gray-700 leading-relaxed mb-5 text-base md:text-lg">
              {service.overview}
            </p>
            <p className="text-gray-700 leading-relaxed mb-10 text-base md:text-lg">
              {service.secondPara}
            </p>
          </div>

          {/* Benefits */}
          <Card className="border-2 border-blue-100 bg-blue-50/30 mb-12">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
                Key Benefits
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {service.benefits.map((b, i) => (
                  <div key={i} className="flex items-start gap-3 text-gray-800">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Process */}
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              What to Expect
            </h2>
            <div className="space-y-4">
              {service.process.map((step, i) => (
                <Card key={i} className="border-2 border-gray-100 hover:border-blue-200 transition-colors">
                  <CardContent className="p-5 md:p-6">
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                        {i + 1}
                      </span>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1 text-base md:text-lg">
                          {step.title}
                        </h3>
                        <p className="text-gray-700 text-sm md:text-base">
                          {step.desc}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Suitable For */}
          <Card className="border-2 border-gray-100 mb-12">
            <CardContent className="p-6 md:p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
                Who Is This For?
              </h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {service.suitableFor.map((s, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Why Choose Us */}
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Why Choose MSK and Sports Physio?
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">HCPC Registered &amp; CSP Member</h4>
                  <p className="text-sm text-gray-600">Fully qualified, regulated and insured.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Award className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">MSc Sports Medicine</h4>
                  <p className="text-sm text-gray-600">Advanced postgraduate qualification.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Same-Day Appointments</h4>
                  <p className="text-sm text-gray-600">Get assessed and treated quickly.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Central Harrow Location</h4>
                  <p className="text-sm text-gray-600">HarroWall Climbing Centre, HA1 4HX.</p>
                </div>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              {service.faqs.map((faq, i) => (
                <Card key={i} className="border-2 border-gray-100 hover:border-blue-200 transition-colors">
                  <CardContent className="p-5 md:p-6">
                    <h3 className="font-bold text-gray-900 mb-2 text-base md:text-lg">
                      {faq.q}
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                      {faq.a}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 md:py-16 px-4 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Book Your {service.name}?
          </h2>
          <p className="text-lg md:text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get expert physiotherapy in Harrow. Same-day appointments often available.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => window.location.href = '/contact'}
              size="lg"
              className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-6 text-base font-semibold rounded-lg shadow-lg"
            >
              Book Your Appointment
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => window.location.href = 'tel:07747075809'}
              size="lg"
              variant="outline"
              className="px-8 py-6 text-base font-semibold rounded-lg border-2 border-white text-white hover:bg-white hover:text-blue-600 bg-transparent"
            >
              <Phone className="mr-2 w-5 h-5" />
              07747075809
            </Button>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 md:py-16 px-4 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
            Explore Our Other Services
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedServices.map(([s, sv]) => {
              const RelIcon = iconMap[sv.icon] || Activity;
              return (
                <Link
                  key={s}
                  to={`/services/${s}`}
                  className="group block"
                  data-testid={`related-service-${s}`}
                >
                  <Card className="border-2 border-gray-200 hover:border-blue-500 hover:shadow-lg transition-all duration-300 h-full">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors">
                          <RelIcon className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                            {sv.name}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {sv.heroLine.replace(' in Harrow', '')}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0 mt-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700"
            >
              View All Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
